import { Component, inject, Inject, NgModule, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CentrexExtensionModel } from 'src/app/models/centrex-extension.model';
import { CentrexExtensionsService } from 'src/app/services/centrex-extensions.service';

import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { HistoriqueComponent } from '../../historique/historique.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { DialerTypes } from 'src/app/models/enums/dialer-types';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { UsersService } from 'src/app/services/users.service';
import { catchError, forkJoin, of } from 'rxjs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { CentrexUsersService } from 'src/app/services/centrex-users-service';

interface ExtensionWithUserInfo extends CentrexExtensionModel {
  firstName?: string;
  lastName?: string;
  email?: string;
}

@Component({
  selector: 'app-extensions-form',
  templateUrl: './extensions-form.component.html',
  styleUrls: ['./extensions-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatDialogModule,
    HistoriqueComponent,
    MatSlideToggleModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCheckboxModule,
    DragDropModule,
    NgxSpinnerModule,
  ],
})
export class ExtensionsFormComponent implements OnInit {
  members: ExtensionWithUserInfo[] = [];

  displayedMemberColumns: string[] = [
    'extension',
    'account',
    'lastName',
    'firstName',
    'email',
    'blocked',
  ];

  mode: 'view' | 'edit' = 'view';
  searchTerm = '';

  availableExtensions: ExtensionWithUserInfo[] = [];
  selectedExtensions: ExtensionWithUserInfo[] = [];
  originalExtension: CentrexExtensionModel = structuredClone(
    this.data.extension,
  );

  dialerTypeOptions = [
    { value: DialerTypes.cyclic, label: 'Cyclique' },
    { value: DialerTypes.sequential, label: 'Séquentiel' },
    { value: DialerTypes.unisson, label: 'Unisson' },
    { value: DialerTypes.oldestidle, label: 'Plus ancien' },
    { value: DialerTypes.none, label: 'Aucun' },
  ];
  form!: FormGroup;

  private spinner = inject(NgxSpinnerService);
  private toastr = inject(ToastrService);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      extension: CentrexExtensionModel;
      organizationId: string;
      platform: string;
      mode: 'view' | 'edit';
      members?: CentrexExtensionModel[];
    },
    private dialogRef: MatDialogRef<ExtensionsFormComponent>,
    private centrexExtensionsService: CentrexExtensionsService,
    private usersService: UsersService,
    private centrexUsersService: CentrexUsersService,
    private fb: FormBuilder,
  ) {
    this.mode = data.mode;
  }
  ngOnInit(): void {
    const extensionIds =
      this.data.platform === 'Centrex Enreach'
        ? (this.data.extension.members?.map((m: any) => m.id) ?? [])
        : (this.data.extension.extensions ?? []);

    console.log('[INIT] Extension IDs du groupe :', extensionIds);

    this.form = this.fb.group({
      name: [{ value: this.data.extension.name, disabled: !this.isEditMode }],
      dialerType: [
        {
          value: this.mapDialerTypeFromApi(
            this.data.extension.dialerType as any,
          ),
          disabled: !this.isEditMode,
        },
      ],
      enabled: [{ value: this.data.extension.enabled, disabled: true }],
    });

    this.spinner.show('spinner');

    // 👉 EDIT MODE
    if (this.isEditMode) {
      forkJoin({
        extensions: this.centrexExtensionsService.getExtensions(
          this.data.organizationId,
          this.data.platform,
        ),
        users: this.centrexUsersService.getAllCentrexUsers(
          this.data.organizationId,
          this.data.platform,
        ),
      }).subscribe({
        next: ({ extensions, users }) => {
          const userMap = new Map(
            users.map((u: any) => [String(u.number).trim(), u]),
          );

          const enrichedExtensions = extensions.map((ext: any) => {
            const user = userMap.get(String(ext.number).trim());

            return {
              ...ext,
              name:
                ext.name ||
                (user
                  ? `${user.firstname || ''} ${user.lastname || ''}`.trim()
                  : '—'),
            };
          });

          this.selectedExtensions = enrichedExtensions.filter((ext) =>
            extensionIds.includes(ext.id),
          );

          this.availableExtensions = enrichedExtensions.filter(
            (ext) =>
              ext.type?.toLowerCase() === 'extension' &&
              !extensionIds.includes(ext.id),
          );

          this.selectedExtensions = [...this.selectedExtensions];
          this.availableExtensions = [...this.availableExtensions];

          this.spinner.hide('spinner');
        },
        error: (err) => {
          console.error(err);
          this.spinner.hide('spinner');
          this.toastr.error('Erreur chargement données');
        },
      });
    }

    // 👉 VIEW MODE
    else {
      console.log('[MODE] Consultation');

      // ENREACH → déjà des objets
      if (
        this.data.platform === 'Centrex Enreach' &&
        this.data.members?.length
      ) {
        const members = this.data.members as ExtensionWithUserInfo[];

        members.forEach((m) => {
          m.firstName = '';
          m.lastName = m.name || '—';
          m.email = '';
        });

        this.members = [...members];
        this.spinner.hide('spinner');
      }

      // NG → faut refetch
      else {
        this.centrexExtensionsService
          .getExtensions(this.data.organizationId, this.data.platform)
          .subscribe({
            next: (extensions) => {
              this.members = extensions.filter((ext: any) =>
                extensionIds.includes(ext.id),
              );

              this.spinner.hide('spinner');
            },
            error: (err) => {
              console.error(err);
              this.spinner.hide('spinner');
              this.toastr.error('Erreur chargement membres');
            },
          });
      }
    }
  }

  private mapDialerTypeToApi(value: DialerTypes): string {
    switch (value) {
      case DialerTypes.cyclic:
        return 'cyclic';
      case DialerTypes.sequential:
        return 'sequential';
      case DialerTypes.unisson:
        return 'unisson';
      case DialerTypes.oldestidle:
        return 'oldestidle';
      default:
        return 'none';
    }
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  private mapDialerTypeFromApi(value: string): DialerTypes {
    switch (value) {
      case 'cyclic':
        return DialerTypes.cyclic;
      case 'sequential':
        return DialerTypes.sequential;
      case 'unisson':
        return DialerTypes.unisson;
      case 'oldestidle':
        return DialerTypes.oldestidle;
      default:
        return DialerTypes.none;
    }
  }

  getDialerTypeLabel(value: number): string {
    switch (value) {
      case DialerTypes.cyclic:
        return 'Cyclique';
      case DialerTypes.sequential:
        return 'Séquentiel';
      case DialerTypes.unisson:
        return 'Unisson'; // FIX
      case DialerTypes.oldestidle:
        return 'Plus ancien';
      case DialerTypes.none:
        return 'Aucun';
      default:
        return '';
    }
  }

  get filteredAvailableExtensions(): CentrexExtensionModel[] {
    const term = this.searchTerm.toLowerCase();
    return this.availableExtensions.filter(
      (ext) =>
        ext.number?.toLowerCase().includes(term) ||
        ext.name?.toLowerCase().includes(term),
    );
  }

  drop(event: CdkDragDrop<ExtensionWithUserInfo[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    // 🔥 FORCE SYNC AVEC TES VARIABLES
    this.selectedExtensions = [...this.selectedExtensions];
    this.availableExtensions = [...this.availableExtensions];

    // 🔥 DEBUG
    console.log(
      'SELECTED NOW:',
      this.selectedExtensions.map((e) => e.number),
    );

    // 🔥 MAJ MODEL
    if (this.data.platform === 'Centrex Enreach') {
      this.data.extension.members = this.selectedExtensions.map((e) => ({
        id: e.id,
        number: e.number,
      }));
    } else {
      this.data.extension.extensions = this.selectedExtensions.map((e) => e.id);
    }
  }

  close(): void {
    if (this.isEditMode) {
      // Restaurer les données si on quitte en edit
      Object.assign(
        this.data.extension,
        structuredClone(this.originalExtension),
      );
    }
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) return;

    this.spinner.show('spinner');
    const updatedExtension: CentrexExtensionModel = {
      ...this.data.extension,
      ...this.form.value,
      dialerType: this.mapDialerTypeToApi(this.form.value.dialerType) as any,
    };

    // GESTION ENREACH
    if (this.data.platform === 'Centrex Enreach') {
      updatedExtension.members = this.selectedExtensions.map((e) => ({
        id: e.id,
        number: e.number,
      }));

      updatedExtension.extensions = null; // IMPORTANT
    }

    // GESTION NG
    else {
      updatedExtension.extensions = this.selectedExtensions.map((e) => e.id);
    }

    this.centrexExtensionsService
      .updateExtension(
        this.data.organizationId,
        this.data.platform,
        updatedExtension.id,
        updatedExtension,
      )
      .subscribe({
        next: () => {
          this.spinner.hide('spinner');
          this.toastr.success('Le groupe a bien été mis à jour.');
          this.dialogRef.close({ updated: true });
        },
        error: (err) => {
          this.spinner.hide('spinner');
          console.error('Erreur mise à jour extension', err);
          this.toastr.error('Erreur lors de la mise à jour du groupe.');
        },
      });
  }
}
